import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSpotify, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useSpotifySlots, useSpotifySlotData } from '../hooks/useSpotifySlots';
import { QuizMusical } from '../components/SpotifyQuiz';
import { NuestraCancion } from '../components/SpotifyOurSong';
import { CompatibilidadMusical } from '../components/SpotifyCompatibility';
import { useIsMobile } from '../hooks/useIsMobile';
import { useIsPWA } from '../hooks/useIsPWA';

// Slot Card Component
const SlotCard = ({
  slotNumber,
  slot,
  slotData,
  onConnect
}: {
  slotNumber: 1 | 2;
  slot: any;
  slotData: any;
  onConnect: () => void;
}) => {
  if (slot.isEmpty) {
    return (
      <div className="bg-white/5 p-6 rounded-lg text-center border-2 border-dashed border-white/20 hover:border-green-400/50 transition-all">
        <div className="text-6xl mb-4">🎵</div>
        <h3 className="text-xl font-bold mb-2">Slot {slotNumber}</h3>
        <p className="mb-4 text-gray-400 text-sm">
          {slotNumber === 1 ? 'Mauricio - Conecta tu Spotify' : 'Invitada - Conecta tu Spotify'}
        </p>
        <button
          onClick={onConnect}
          className="flex items-center justify-center gap-2 py-3 px-6 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-all hover:scale-105 mx-auto"
        >
          <FaSpotify />
          Conectar Spotify
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-green-500/20 to-purple-500/20 p-6 rounded-lg text-center border-2 border-green-400/50">
      <div className="relative inline-block mb-3">
        <img
          src={slotData?.profile?.images?.[0]?.url || `https://via.placeholder.com/80x80/1DB954/white?text=${slot.displayName.charAt(0)}`}
          alt={slot.displayName}
          className="w-20 h-20 rounded-full border-2 border-green-400"
        />
        <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
          {slotNumber}
        </div>
      </div>
      <h3 className="text-lg font-bold text-green-400">{slot.displayName}</h3>
      <p className="text-xs text-gray-400 mb-2">Spotify ID: {slot.spotifyId}</p>
      {slot.connectedBy && (
        <p className="text-xs text-gray-500">Conectado por: {slot.connectedBy}</p>
      )}
      {slotData?.profile && (
        <p className="text-sm text-gray-400 mt-2">{slotData.profile.followers?.total.toLocaleString()} seguidores</p>
      )}
    </div>
  );
};

// Music Data Panel
const MusicDataPanel = ({
  title,
  items,
  loading,
  type
}: {
  title: string;
  items: any[];
  loading: boolean;
  type: 'tracks' | 'playlists';
}) => {
  if (loading) {
    return (
      <div className="bg-white/5 p-4 rounded-lg">
        <h4 className="font-bold mb-3 text-green-400">{title}</h4>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 p-4 rounded-lg">
      <h4 className="font-bold mb-3 text-green-400">{title}</h4>
      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
        {items.length > 0 ? (
          items.slice(0, type === 'tracks' ? 10 : 20).map((item, index) => (
            <div key={item.id} className="flex items-center gap-3 bg-white/5 p-2 rounded-md hover:bg-white/10 transition-colors">
              {type === 'tracks' && (
                <span className="text-green-400 font-bold text-lg w-6">{index + 1}</span>
              )}
              <img
                src={
                  type === 'tracks'
                    ? item.album.images?.[0]?.url || `https://via.placeholder.com/40x40/1DB954/white?text=♪`
                    : item.images?.[0]?.url || `https://via.placeholder.com/48x48/1DB954/white?text=${item.name.charAt(0)}`
                }
                alt={item.name}
                className={`${type === 'tracks' ? 'w-10 h-10' : 'w-12 h-12'} rounded object-cover`}
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{item.name}</p>
                <p className="text-xs text-gray-400 truncate">
                  {type === 'tracks'
                    ? item.artists.map((a: any) => a.name).join(', ')
                    : `${item.tracks?.total || 0} canciones`
                  }
                </p>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-400 text-sm">No hay datos disponibles</p>
        )}
      </div>
    </div>
  );
};

// Main Component
export const NewSpotifySection = () => {
  const { slot1, slot2, connectSlot, loading: authLoading, error: authError } = useSpotifySlots();
  const { slot1Data, slot2Data, loading: dataLoading } = useSpotifySlotData(slot1, slot2);

  const isMobile = useIsMobile();
  const isPWA = useIsPWA();
  const useMobileLayout = isMobile && isPWA;

  // Collapsible state for datos musicales - collapsed on mobile by default
  const [slot1Expanded, setSlot1Expanded] = useState(!isMobile);
  const [slot2Expanded, setSlot2Expanded] = useState(!isMobile);

  // Mapear datos para compatibilidad con componentes existentes
  const myAccessToken = slot1Data?.accessToken || null;
  const herAccessToken = slot2Data?.accessToken || null;
  const myProfile = slot1Data?.profile || null;
  const myTopTracks = slot1Data?.topTracks || [];
  const myTopArtists = slot1Data?.topArtists || [];
  const myPlaylists = slot1Data?.playlists || [];
  const herProfile = slot2Data?.profile || null;
  const herTopTracks = slot2Data?.topTracks || [];
  const herTopArtists = slot2Data?.topArtists || [];

  return (
    <section id="spotify" className="relative min-h-screen flex items-center py-20 bg-black p-4">
      <div className="max-w-6xl w-full mx-auto text-white space-y-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center">
          <h2 className={`text-4xl sm:text-5xl font-bold mb-4 ${useMobileLayout ? 'pt-4' : 'pt-8'}`}>
            🎵 Nuestra Música
          </h2>
          <p className="text-gray-400">Conecten sus cuentas de Spotify y compartan sus gustos musicales</p>
        </motion.div>

        {/* Error de autenticación */}
        {authError && (
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
            <p className="text-red-400">❌ Error de autenticación: {authError}</p>
          </div>
        )}

        <div className="bg-white/10 rounded-2xl shadow-2xl p-6 sm:p-8 space-y-8">

          {/* ===== 1. SLOTS LOGIN SECTION ===== */}
          <div>
            <h3 className="text-2xl font-bold mb-4 text-center">🔐 Conectar Cuentas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                <SlotCard
                  slotNumber={1}
                  slot={slot1}
                  slotData={slot1Data}
                  onConnect={() => connectSlot(1)}
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <SlotCard
                  slotNumber={2}
                  slot={slot2}
                  slotData={slot2Data}
                  onConnect={() => connectSlot(2)}
                />
              </motion.div>
            </div>
          </div>

          {/* ===== 2. MUSIC DATA SECTION ===== */}
          {(!slot1.isEmpty || !slot2.isEmpty) && (
            <>
              <div className="border-t border-white/20 pt-8">
                <h3 className="text-2xl font-bold mb-4 text-center">🎶 Datos Musicales</h3>

                {/* Slot 1 Data */}
                {!slot1.isEmpty && slot1Data && (
                  <div className="mb-8">
                    <button
                      onClick={() => setSlot1Expanded(!slot1Expanded)}
                      className="w-full flex items-center justify-between text-xl font-semibold mb-4 text-green-400 hover:text-green-300 transition-colors group"
                    >
                      <span>📊 Datos de {slot1.displayName}</span>
                      <motion.div
                        animate={{ rotate: slot1Expanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-green-400 group-hover:text-green-300"
                      >
                        <FaChevronDown />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {slot1Expanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <MusicDataPanel
                          title="🎵 Top Canciones"
                          items={slot1Data.topTracks || []}
                          loading={dataLoading}
                          type="tracks"
                        />
                      </motion.div>
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <MusicDataPanel
                          title="📝 Playlists"
                          items={slot1Data.playlists || []}
                          loading={dataLoading}
                          type="playlists"
                        />
                      </motion.div>
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="bg-white/5 p-4 rounded-lg">
                          <h4 className="font-bold mb-3 text-green-400">👥 Top Artistas</h4>
                          <div className="space-y-2">
                            {dataLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
                              </div>
                            ) : (slot1Data.topArtists || []).slice(0, 5).map((artist: any, index: number) => (
                              <div key={artist.id} className="flex items-center gap-2 text-sm">
                                <span className="text-green-400 font-bold w-6">{index + 1}</span>
                                <span className="truncate">{artist.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Slot 2 Data */}
                {!slot2.isEmpty && slot2Data && (
                  <div>
                    <button
                      onClick={() => setSlot2Expanded(!slot2Expanded)}
                      className="w-full flex items-center justify-between text-xl font-semibold mb-4 text-purple-400 hover:text-purple-300 transition-colors group"
                    >
                      <span>📊 Datos de {slot2.displayName}</span>
                      <motion.div
                        animate={{ rotate: slot2Expanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-purple-400 group-hover:text-purple-300"
                      >
                        <FaChevronDown />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {slot2Expanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <MusicDataPanel
                          title="🎵 Top Canciones"
                          items={slot2Data.topTracks || []}
                          loading={dataLoading}
                          type="tracks"
                        />
                      </motion.div>
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <MusicDataPanel
                          title="📝 Playlists"
                          items={slot2Data.playlists || []}
                          loading={dataLoading}
                          type="playlists"
                        />
                      </motion.div>
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="bg-white/5 p-4 rounded-lg">
                          <h4 className="font-bold mb-3 text-purple-400">👥 Top Artistas</h4>
                          <div className="space-y-2">
                            {dataLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
                              </div>
                            ) : (slot2Data.topArtists || []).slice(0, 5).map((artist: any, index: number) => (
                              <div key={artist.id} className="flex items-center gap-2 text-sm">
                                <span className="text-purple-400 font-bold w-6">{index + 1}</span>
                                <span className="truncate">{artist.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== 3. SPOTIFY FEATURES SECTION ===== */}
          {!slot1.isEmpty && !slot2.isEmpty && (
            <>
              <div className="border-t border-white/20 pt-8">
                <h3 className="text-2xl font-bold mb-6 text-center">✨ Funciones Especiales</h3>

                {/* Compatibilidad Musical */}
                <CompatibilidadMusical
                  myTopTracks={myTopTracks}
                  myTopArtists={myTopArtists}
                  herTopTracks={herTopTracks}
                  herTopArtists={herTopArtists}
                />

                {/* Quiz Musical */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
                  <QuizMusical
                    myTracks={myTopTracks}
                    herTracks={herTopTracks}
                    myProfile={myProfile}
                    herProfile={herProfile}
                  />
                </motion.div>

                {/* Nuestra Canción */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
                  <NuestraCancion
                    myTracks={myTopTracks}
                    herTracks={herTopTracks}
                    myProfile={myProfile}
                    herProfile={herProfile}
                  />
                </motion.div>
              </div>
            </>
          )}

          {/* Message when only one or no slots connected */}
          {(slot1.isEmpty || slot2.isEmpty) && (
            <div className="text-center py-8 text-gray-400">
              <p className="text-lg">
                {slot1.isEmpty && slot2.isEmpty
                  ? '👆 Conecten ambas cuentas para ver todas las funciones especiales'
                  : '👆 Conecten la segunda cuenta para desbloquear todas las funciones especiales'
                }
              </p>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};
